import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { ApplicantProfileComments } from "@/pages/recruit/candidates/components/ApplicantProfileComments.tsx";
import { ApplicantProfileFields } from "@/pages/recruit/candidates/components/ApplicantProfileFields.tsx";
import ApplicantProfileCV from "@/pages/recruit/candidates/components/ApplicantProfileCV.tsx";
import { useCandidatesByApplicant } from "@/queries/recruit/use-candidates.query.ts";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import { ApiCFEntityTypes, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiApplicant, ApiApplicantShort, ApiCandidate } from "@/types/recruit/applicant.types.ts";
import { getStageEmoji } from "@/types/recruit/stages.types.ts";
import {
  Avatar,
  Box,
  ButtonIcon,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  TabItem,
  Tabs,
  Tag,
  Text,
  Title,
  toCssColorValue,
} from "@skailer-com/uikit";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface ApplicantMergeProfileCompareProps {
  applicant: ApiApplicant;
  similarApplicantIndex?: number;
  similarApplicants?: ApiApplicantShort[];
  onChangeSimilarIndex?: (index: number) => void;
  fixedHeaderHeight?: boolean;
}

export const ApplicantMergeProfileCompare = ({
  applicant,
  similarApplicantIndex,
  similarApplicants,
  onChangeSimilarIndex,
  fixedHeaderHeight = true,
}: ApplicantMergeProfileCompareProps) => {
  const { t } = useTranslation();
  const { data: candidates } = useCandidatesByApplicant(applicant.id);
  const { hasField, isPending: isConfigPending } = useEntityFormConfig(ApiCFEntityTypes.applicant);

  const handleSelectSimilarProfile = useCallback(
    (delta: number) => {
      if (!similarApplicants || !similarApplicantIndex) return;

      let nextIndex = similarApplicantIndex - 1 + delta;
      if (nextIndex >= similarApplicants.length) nextIndex = 0;
      else if (nextIndex < 0) nextIndex = 0;
      onChangeSimilarIndex?.(nextIndex);
    },
    [similarApplicantIndex, similarApplicants]
  );

  return (
    <Stack gap={"lg"} className={twMerge("basis-1/2")}>
      <Box className={twMerge(fixedHeaderHeight && "h-[420px]")}>
        <Stack gap={"md"} className={"w-full px-6 pt-6"} items={"center"}>
          <Avatar
            size={"2xl"}
            url={applicant.photo_url}
            variant={"opaque"}
            acronym={`${applicant.first_name} ${applicant.last_name}`}
          />

          {isConfigPending ? (
            <Stack items={"center"} gap={"sm"}>
              <Skeleton className={"h-7 w-48"} />
              <Skeleton className={"h-5 w-32"} />
            </Stack>
          ) : (
            <Stack items={"center"} className={"text-lg text-center"}>
              <Title
                header={[
                  applicant.first_name,
                  hasField(ApiFieldTypes.applicant_middle_name) ? applicant.middle_name : undefined,
                  applicant.last_name,
                ]
                  .filter((name) => name?.trim())
                  .join(" ")}
                size={"lg"}
              />
              {hasField(ApiFieldTypes.applicant_position) &&
                applicant.current_position.position && (
                  <Text className={"text-secondary"}>{applicant.current_position.position}</Text>
                )}
              {hasField(ApiFieldTypes.applicant_company) && applicant.current_position.company && (
                <Text className={"text-secondary"}>{applicant.current_position.company}</Text>
              )}
            </Stack>
          )}

          <Box className={"p-lg"}>
            <Stack direction={"horizontal"} gap={"sm"}>
              {applicant.labels.map((label, index) => (
                <Tag color={toCssColorValue(label.color)} key={`applicant-tag-${index}`}>
                  {label.title}
                </Tag>
              ))}
            </Stack>
          </Box>
        </Stack>
        {similarApplicantIndex && similarApplicants && (
          <Stack items={"center"} justify={"center"}>
            <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
              <ButtonIcon
                icon={<FontAwesomeIcon icon={"fa-light fa-chevron-left"} />}
                variant={"secondary"}
                onClick={() => {
                  handleSelectSimilarProfile(-1);
                }}
              />
              <Text className={"text-title"}>
                {`${t("ats:similar_candidate")}: ${t("common:of", { current: similarApplicantIndex, total: similarApplicants.length })}`}
              </Text>
              <ButtonIcon
                icon={<FontAwesomeIcon icon={"fa-light fa-chevron-right"} />}
                variant={"secondary"}
                onClick={() => {
                  handleSelectSimilarProfile(1);
                }}
              />
            </Stack>
          </Stack>
        )}
      </Box>
      <ApplicantProfileFields applicant={applicant} customFields={applicant.custom_fields} />
      {candidates && candidates.length > 0 && (
        <Stack gap={"lg"}>
          {candidates.map((candidate: ApiCandidate) => (
            <Box key={`candidate-${candidate.id}`}>
              <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
                {candidate.disqualification_reason ? (
                  candidate.reserved === true ? (
                    <Avatar
                      url={null}
                      emoji={emojiDefaults.stages.reserved}
                      key={"avatar_disqualified"}
                    />
                  ) : (
                    <Avatar
                      url={null}
                      emoji={emojiDefaults.stages.disqualified}
                      key={"avatar_disqualified"}
                    />
                  )
                ) : (
                  <Avatar
                    url={null}
                    emoji={getStageEmoji(candidate.pipeline_stage)}
                    key={"avatar_qualified"}
                  />
                )}
                <Stack>
                  <Text size={"lg"} className={"text-title"}>
                    {candidate.disqualification_reason == null
                      ? candidate.pipeline_stage.title
                      : t("ats:disqualified")}
                  </Text>
                  <Text className={"text-secondary"}>{candidate.job.title}</Text>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
      <Stack gap={"sm"} className={"grow pb-6"}>
        <Tabs>
          {applicant.cvs?.map((cv, index) => (
            <TabItem title={cv.source.title} key={`resume-tab-${index}`}>
              <ApplicantProfileCV applicant={applicant} cv={cv} />
            </TabItem>
          ))}
          <TabItem title={t("core:notes")}>
            {applicant && <ApplicantProfileComments disabledComments applicant={applicant} />}
          </TabItem>
        </Tabs>
      </Stack>
    </Stack>
  );
};
